<script setup lang="ts">
  import type { Article } from '../../types'

  const { t } = useI18n()
  export type ArticleGroupProps = {
    design?: 'google' | 'msn'
    showBorder?: boolean
    articles: Article[]
    type?: 'related-stories' | 'top-stories'
  }

  const props = withDefaults(defineProps<ArticleGroupProps>(), {
    design: 'google',
    type: 'related-stories',
    showBorder: false,
  })

  defineEmits<{
    (e: 'article-click', article: Article): void
  }>()

  const header = computed(() => {
    if (props.design === 'msn' && props.type === 'top-stories')
      return { title: 'Top Stories', iconName: 'fire' }
    if (props.design === 'msn' && props.type === 'related-stories')
      return {
        title: t('Related Stories'),
        iconName: 'related-stories',
      }
    return {}
  })

  const config = useRuntimeConfig()
</script>

<template>
  <div
    v-if="design === 'google'"
    class="flex w-[300px] flex-col justify-evenly gap-4"
    data-testid="article-group-google"
  >
    <ContentCard
      v-for="(article, index) in articles"
      :key="article.articleId"
      :tile-position="index + 1"
      design="google"
      variant="default"
      size="tiny"
      tile-type="article-group"
      v-bind="article"
      @click="$emit('article-click', article)"
    />
  </div>
  <div
    v-else-if="design === 'msn' && articles.length <= 3"
    class="rounded-8px shadow-article-card bg-background-default w-[300px] h-[304px]"
    :class="{
      'border-8px border border-border-neutral-subtle': showBorder,
    }"
    data-testid="article-group-msn"
  >
    <div class="flex flex-row gap-8px px-16px pt-16px pb-8px items-center">
      <img
        v-if="header.iconName"
        :src="`${config.app.cdnURL}/icons/${header.iconName}.svg`"
        alt="fire icon"
        class="h-16px w-16px dark:filter dark:invert"
      />
      <Typography
        color="text-content-primary"
        element="h2"
        weight="font-semibold"
        line-height="leading-[20px]"
      >
        {{ header.title }}
      </Typography>
    </div>
    <div class="flex flex-col px-8px gap-6px">
      <ContentCard
        v-for="(article, index) in articles"
        :key="article.articleId"
        :tile-position="index"
        design="msn"
        variant="default"
        size="tiny"
        tile-type="article-group"
        v-bind="article"
        @click="$emit('article-click', article)"
      />
    </div>
  </div>
  <ArticleGroupSlider
    v-else-if="design === 'msn'"
    :articles="articles"
    :title="header.title"
    :icon-name="header.iconName"
    :show-border="showBorder"
    @article-click="(article) => $emit('article-click', article)"
  />
</template>
